import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FiUpload } from "react-icons/fi";
import { useSelector } from "react-redux";
import { getApiData, postFormData } from "../../../../utils/APIHelper";
import { toast } from "react-toastify";
import moment from "moment";
import { getStatusStyles, getPriorityStyles, formatEnumForDisplay, getMarkupFromMetadata, ISSUE_STATUS } from "./components/constants";

const STATUS_OPTIONS = [
  { value: ISSUE_STATUS.OPEN, label: formatEnumForDisplay(ISSUE_STATUS.OPEN) },
  { value: ISSUE_STATUS.IN_PROGRESS, label: formatEnumForDisplay(ISSUE_STATUS.IN_PROGRESS) },
  { value: ISSUE_STATUS.DONE, label: formatEnumForDisplay(ISSUE_STATUS.DONE) },
  { value: ISSUE_STATUS.RESOLVED, label: formatEnumForDisplay(ISSUE_STATUS.RESOLVED) },
];

function parseResolvedMetadata(resolved_metadata) {
  if (resolved_metadata == null) return null;
  try {
    return typeof resolved_metadata === "string"
      ? JSON.parse(resolved_metadata)
      : resolved_metadata;
  } catch {
    return null;
  }
}

export default function IssueDetailModal({ open, issueId, onClose, onSuccess }) {
  const { userData } = useSelector((state) => state.auth || {});
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [updating, setUpdating] = useState(false);
  const [resolveComments, setResolveComments] = useState("");
  const [resolveImageFile, setResolveImageFile] = useState(null);
  const [resolveImagePreview, setResolveImagePreview] = useState(null);
  const resolveFileInputRef = useRef(null);

  useEffect(() => {
    if (!open || !issueId) {
      setIssue(null);
      setSelectedStatus("");
      setResolveComments("");
      setResolveImageFile(null);
      setResolveImagePreview(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getApiData(`api/v2/issue-logs/${issueId}`, "GET", null, null, true)
      .then((response) => {
        if (cancelled) return;
        const resolved = response?.data ?? response ?? null;
        setIssue(resolved);
        const status = (resolved?.status ?? "") || "";
        setSelectedStatus(status);
      })
      .catch(() => {
        if (!cancelled) setIssue(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, issueId]);

  const handleResolveImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResolveImageFile(file);
    setResolveImagePreview(URL.createObjectURL(file));
    if (resolveFileInputRef.current) resolveFileInputRef.current.value = "";
  };

  const handleResolveImageRemove = () => {
    setResolveImageFile(null);
    if (resolveImagePreview) {
      URL.revokeObjectURL(resolveImagePreview);
    }
    setResolveImagePreview(null);
    if (resolveFileInputRef.current) resolveFileInputRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (!issue || !selectedStatus) {
      toast.error("Please select a status");
      return;
    }
    const isResolving = selectedStatus === ISSUE_STATUS.RESOLVED;
    const alreadyResolved = (issue?.status ?? "") === ISSUE_STATUS.RESOLVED;

    if (isResolving && !alreadyResolved) {
      if (!(resolveComments ?? "").trim()) {
        toast.error("Comments are required when resolving an issue");
        return;
      }
      if (!resolveImageFile) {
        toast.error("Please upload an image when resolving an issue");
        return;
      }
    }

    try {
      setUpdating(true);
      const body = { ...issue, status: selectedStatus };
      const putResponse = await getApiData(
        `api/v2/issue-logs/${issueId}`,
        "PUT",
        body,
        { "Content-Type": "application/json" },
        true
      );
      if (putResponse?.success === false) {
        toast.error(putResponse?.message || "Failed to update status");
        return;
      }

      if (isResolving && !alreadyResolved && resolveImageFile) {
        const formData = new FormData();
        formData.append("comments", (resolveComments ?? "").trim());
        formData.append("resolvedBy", String(userData?.id ?? ""));
        formData.append("image", resolveImageFile, resolveImageFile.name || "image.jpg");
        const resolveResponse = await postFormData(
          `api/v2/issue-logs/${issueId}/resolve`,
          formData,
          {},
          true
        );
        if (resolveResponse?.success === false && resolveResponse?.id == null) {
          toast.error(resolveResponse?.message || "Failed to submit resolution");
          return;
        }
      }

      toast.success("Status updated");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err?.message || "Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const handleClose = () => {
    setSelectedStatus("");
    setResolveComments("");
    handleResolveImageRemove();
    onClose();
  };

  if (!open) return null;

  const displayIssue = issue;
  const imageUrl = displayIssue?.image_url;
  const status = displayIssue?.status ?? "";
  const severity = displayIssue?.issue_category?.severity ?? "-";
  const markup = getMarkupFromMetadata(displayIssue?.metadata);
  const isResolved = status === ISSUE_STATUS.RESOLVED;
  const resolvedMeta = parseResolvedMetadata(displayIssue?.resolved_metadata);
  const showResolveForm = selectedStatus === ISSUE_STATUS.RESOLVED && !isResolved;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        <span style={{ fontWeight: 500, fontSize: 18 }}>Issue Details</span>
        <IconButton size="small" onClick={handleClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <CircularProgress size={32} />
          </div>
        ) : displayIssue ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              {imageUrl && (
                <div style={{ flexShrink: 0, position: "relative" }}>
                  <img
                    src={imageUrl}
                    alt="Issue"
                    style={{ width: 200, height: 160, borderRadius: 8, border: "1px solid #e5e7eb", objectFit: "cover" }}
                  />
                  {markup != null && (
                    <span
                      style={{
                        position: "absolute",
                        bottom: 4,
                        right: 4,
                        minWidth: 22,
                        height: 22,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "50%",
                        backgroundColor: "#2563eb",
                        color: "#fff",
                        fontSize: 12,
                        fontWeight: 500,
                        padding: "0 6px",
                      }}
                    >
                      {markup}
                    </span>
                  )}
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ color: "#0d0d0e", fontWeight: 500, fontSize: 14, margin: 0 }}>{displayIssue.description || "-"}</p>
                {displayIssue.issue_category && (
                  <p style={{ fontSize: 14, color: "#949DAA", margin: "4px 0 0 0" }}>
                    {displayIssue.issue_category.activityType} • {displayIssue.issue_category.category}
                  </p>
                )}
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, fontSize: 14 }}>
              <div>
                <span style={{ color: "#949DAA", fontSize: 14 }}>Source</span>
                <p style={{ color: "#0d0d0e", margin: "4px 0 0 0" }}>{formatEnumForDisplay(displayIssue.source_type) || "-"}</p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ color: "#949DAA", fontSize: 14 }}>Status</span>
                <span className={`inline-flex w-fit capitalize items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusStyles(status)}`}>
                  {formatEnumForDisplay(status) || "-"}
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ color: "#949DAA", fontSize: 14 }}>Severity</span>
                <span className={`inline-flex w-fit capitalize px-2.5 py-1 rounded-full text-xs font-medium ${getPriorityStyles(severity)}`}>
                  {formatEnumForDisplay(severity) || severity}
                </span>
              </div>
              <div>
                <span style={{ color: "#949DAA", fontSize: 14 }}>Date Logged</span>
                <p style={{ color: "#0d0d0e", margin: "4px 0 0 0" }}>
                  {displayIssue.created_at ? moment(displayIssue.created_at).format("MMM DD, YYYY") : "-"}
                </p>
              </div>
            </div>

            {/* Resolved Details (when already resolved) */}
            {isResolved && resolvedMeta && (
              <div style={{ marginTop: 8, padding: 16, backgroundColor: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0" }}>
                <p style={{ fontWeight: 600, fontSize: 14, color: "#0d0d0e", margin: "0 0 12px 0" }}>Resolved Details</p>
                {resolvedMeta.comments && (
                  <div style={{ marginBottom: 12 }}>
                    <p style={{ fontSize: 12, color: "#64748b", margin: "0 0 4px 0" }}>Comments</p>
                    <p style={{ fontSize: 14, color: "#0d0d0e", margin: 0 }}>{resolvedMeta.comments}</p>
                  </div>
                )}
                {resolvedMeta.imageUrl && (
                  <div>
                    <p style={{ fontSize: 12, color: "#64748b", margin: "0 0 8px 0" }}>Resolution image</p>
                    <img
                      src={resolvedMeta.imageUrl}
                      alt="Resolution"
                      style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8, objectFit: "contain", border: "1px solid #e2e8f0" }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Update Status + Resolve form (when not yet resolved or status changed to RESOLVED) */}
            <div style={{ paddingTop: 8, marginTop: 8 }}>
              <FormControl fullWidth size="small" sx={{ borderRadius: "12px" }}>
                <InputLabel id="issue-status-label">Update Status</InputLabel>
                <Select
                  labelId="issue-status-label"
                  value={selectedStatus}
                  label="Status"
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  sx={{ borderRadius: "12px" }}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {showResolveForm && (
                <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 16 }}>
                  <div>
                    <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#0d0d0e", marginBottom: 8 }}>Comments *</label>
                    <textarea
                      value={resolveComments}
                      onChange={(e) => setResolveComments(e.target.value)}
                      placeholder="Add resolution comments..."
                      rows={4}
                      style={{
                        width: "100%",
                        padding: 12,
                        borderRadius: 12,
                        border: "1px solid #d1d5db",
                        fontSize: 14,
                        fontFamily: "inherit",
                        resize: "vertical",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <div>
                    <input
                      ref={resolveFileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleResolveImageChange}
                    />
                    <Button
                      component="span"
                      variant="outlined"
                      fullWidth
                      startIcon={<FiUpload style={{ fontSize: 18 }} />}
                      onClick={() => resolveFileInputRef.current?.click()}
                      disabled={updating}
                      sx={{
                        textTransform: "none",
                        borderColor: "#4D6BF6",
                        color: "#4D6BF6",
                        borderRadius: "12px",
                        py: 1.5,
                        "&:hover": { borderColor: "#3b5bd9", backgroundColor: "rgba(77, 107, 246, 0.04)" },
                      }}
                      className="btn btn-outline btn-primary gap-2 w-full"
                    >
                      Upload Image
                    </Button>
                    {resolveImagePreview && (
                      <div style={{ marginTop: 12, position: "relative", display: "inline-block" }}>
                        <img
                          src={resolveImagePreview}
                          alt="Preview"
                          style={{ maxWidth: "100%", maxHeight: 180, borderRadius: 8, border: "1px solid #e5e7eb", objectFit: "contain" }}
                        />
                        <IconButton
                          size="small"
                          onClick={handleResolveImageRemove}
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            backgroundColor: "rgba(0,0,0,0.5)",
                            color: "#fff",
                            "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 24 }}>
                <Button variant="outlined" onClick={handleClose} sx={{ textTransform: "none" }}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={updating || !selectedStatus || (showResolveForm && (!(resolveComments ?? "").trim() || !resolveImageFile))}
                  sx={{ backgroundColor: "#4D6BF6", textTransform: "none" }}
                >
                  {updating ? "Saving..." : "Submit"}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <p style={{ color: "#949DAA", padding: 16 }}>Issue not found.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
